/**
 * 後台 — 出貨預估(#15 回購預測 B2B 版)
 *
 * 老闆視角:「我欠客戶多少瓶還沒出(存摺未出貨餘額)?這週 / 這個月大概要出多少?」好對工廠叫貨。
 *   品類卡 — 每品類:未出貨餘額、持有人數 / 近 N 週未提領人數、本週 / 本月 已出與預計、上月已出
 *   會員明細 — 持有人逐位:各品類餘額、預計本週 / 本月、最後出貨日;近 N 週完全沒提領的標「近期未提領」
 * 預計出貨 = 依近 N 週出貨速率推估(供叫貨參考),不是 AI 預測。
 * 單位不互換:即飲瓶、留白組、燕盞盒、禮盒 / 紙袋個,各品類各算各的。
 * 資料來源 GET /api/admin/forecast?weeks=N;匯出 POST /api/admin/forecast/export(品類彙總 + 會員明細)。
 *
 * Route: /admin/forecast
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminHeader from "../../components/AdminHeader";
import DataTable, { type Column } from "../../components/DataTable";
import TierBadge, { type Tier } from "../../components/TierBadge";
import {
  adminExportForecast,
  adminGetForecast,
  type ForecastCategory,
  type ForecastData,
  type ForecastMemberCat,
  type ForecastMemberRow,
} from "../../api/admin";
import { useAuthStore } from "../../store/authStore";
import { useUIStore } from "../../store/uiStore";
import { todayTpeCompact } from "../../utils/date";


const WINDOW_OPTIONS = [4, 8, 12];

// 這些品類全 0 就不顯示卡片(禮盒 / 紙袋是內部送禮包材;既有餘額是舊制,沒有就不用提)
const HIDE_WHEN_EMPTY = new Set(["box", "bag", "legacy"]);

function isEmptyCategory(c: ForecastCategory): boolean {
  return (
    c.total_balance === 0 &&
    c.shipped_this_week === 0 &&
    c.shipped_this_month === 0 &&
    c.shipped_last_month === 0 &&
    c.projected_week === 0 &&
    c.projected_month === 0
  );
}


export default function Forecast() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const showToast = useUIStore((s) => s.showToast);

  const [weeks, setWeeks] = useState<number>(8);
  const [data, setData] = useState<ForecastData | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (role !== "admin") {
      navigate("/admin/login", { replace: true });
      return;
    }
    // 連點切換視窗時,舊請求晚到不能蓋掉新結果
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await adminGetForecast(weeks);
        if (!cancelled) setData(r);
      } catch {
        if (!cancelled) showToast("出貨預估載入失敗", "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, weeks]);

  async function exportExcel() {
    setExporting(true);
    try {
      const blob = await adminExportForecast(weeks);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `出貨預估_${todayTpeCompact()}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      showToast("已匯出出貨預估", "success");
    } catch {
      showToast("匯出失敗,請再試一次", "error");
    } finally {
      setExporting(false);
    }
  }

  const categories = data?.categories ?? [];
  const visibleCategories = categories.filter((c) => !(HIDE_WHEN_EMPTY.has(c.category) && isEmptyCategory(c)));

  // 一個會員的某個欄位 → 逐品類「即飲 12 瓶」;pick 回 0 的略過(餘額欄不會有 0,預計欄 0 就不列)
  function catLines(m: ForecastMemberRow, pick: (c: ForecastMemberCat) => number) {
    const lines: { key: string; text: string }[] = [];
    for (const cat of categories) {
      const cv = m.cats[cat.category];
      if (!cv) continue;
      const n = pick(cv);
      if (n > 0) lines.push({ key: cat.category, text: `${cat.name} ${n.toLocaleString()} ${cat.unit}` });
    }
    return lines;
  }

  function renderLines(lines: { key: string; text: string }[]) {
    if (lines.length === 0) return <span className="text-muted">—</span>;
    return (
      <div>
        {lines.map((l) => (
          <div key={l.key} className="whitespace-nowrap">{l.text}</div>
        ))}
      </div>
    );
  }

  const columns: Column<ForecastMemberRow>[] = [
    {
      key: "name",
      header: "姓名",
      render: (r) => (
        <span className="whitespace-nowrap">
          <span className="font-medium text-ink">{r.name || "—"}</span>
          {r.idle && <span className="ml-2 text-xxs text-coral">近期未提領</span>}
        </span>
      ),
    },
    { key: "phone", header: "手機", render: (r) => r.phone || "—" },
    { key: "tier", header: "等級", render: (r) => <TierBadge tier={(r.tier as Tier) || "general"} size="sm" /> },
    { key: "balance", header: "未出貨餘額", render: (r) => renderLines(catLines(r, (c) => c.balance)) },
    { key: "pweek", header: "預計本週", render: (r) => renderLines(catLines(r, (c) => c.projected_week)) },
    { key: "pmonth", header: "預計本月", render: (r) => renderLines(catLines(r, (c) => c.projected_month)) },
    {
      key: "last",
      header: "最後出貨日",
      render: (r) => (r.last_shipped_at ? String(r.last_shipped_at).slice(0, 10) : "—"),
    },
  ];

  return (
    <div className="min-h-screen bg-cream pb-12">
      <AdminHeader active="forecast" />

      <div className="p-6 space-y-5">
        {/* 推估視窗 + 匯出 */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="text-sm text-muted">推估視窗</div>
            <div className="flex gap-1 bg-white rounded-lg p-1 border border-line">
              {WINDOW_OPTIONS.map((w) => (
                <button
                  key={w}
                  onClick={() => setWeeks(w)}
                  className={`text-xs px-3 py-1 rounded transition ${
                    weeks === w ? "bg-primary text-white" : "text-muted hover:bg-cream"
                  }`}
                >
                  近 {w} 週
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={exportExcel}
            disabled={exporting || !data}
            className="btn-primary disabled:opacity-50"
          >
            {exporting ? "匯出中…" : "匯出 Excel"}
          </button>
        </div>

        <div className="text-xs text-muted">
          未出貨餘額為會員存摺尚未提領的量;預計出貨依近 {weeks} 週出貨速率推估,供叫貨參考。
        </div>

        {!data ? (
          <div className="p-10 text-center text-muted">{loading ? "載入中…" : "無資料"}</div>
        ) : (
          <div className={`space-y-5 transition-opacity ${loading ? "opacity-60" : ""}`}>
            {/* 品類卡 */}
            {visibleCategories.length === 0 ? (
              <div className="card text-center text-muted text-sm py-8">目前沒有未出貨餘額</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {visibleCategories.map((c) => (
                  <CategoryCard key={c.category} c={c} weeks={weeks} />
                ))}
              </div>
            )}

            {/* 會員明細 */}
            <div className="card">
              <div className="flex items-baseline justify-between mb-3">
                <div className="text-sm font-bold text-ink">
                  會員明細
                  <span className="ml-2 text-xs font-normal text-muted">({data.holders_total} 位持有人,依餘額由多到少)</span>
                </div>
                <div className="text-xxs text-muted">
                  資料時間 {String(data.as_of).slice(0, 16).replace("T", " ")}
                </div>
              </div>
              <DataTable
                columns={columns}
                rows={data.members}
                onRowClick={(r) => navigate(`/admin/members/${r.line_user_id}`)}
                emptyHint="目前沒有持有未出貨餘額的會員"
              />
              <div className="text-xxs text-muted mt-3">
                各品類單位不同,不互相加總;預計 = 已出 + 依速率推估還會再出的量,再出的部分不會超過該會員的餘額。
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ── 子元件 ──────────────────────────────────

function CategoryCard({ c, weeks }: { c: ForecastCategory; weeks: number }) {
  return (
    <div className="card">
      <div className="text-xs text-muted">{c.name}未出貨餘額</div>
      <div className="flex items-baseline gap-1 mt-1">
        <span className="text-2xl font-bold text-ink">{c.total_balance.toLocaleString()}</span>
        <span className="text-xs text-muted">{c.unit}</span>
      </div>
      <div className="text-xs text-muted mt-1">
        持有人 {c.holders} 人 · 近 {weeks} 週未提領{" "}
        <span className={c.idle_holders > 0 ? "text-coral font-medium" : ""}>{c.idle_holders}</span> 人
      </div>

      <div className="mt-3 pt-3 border-t border-line space-y-1.5 text-sm">
        <FlowRow label="本週" shipped={c.shipped_this_week} projected={c.projected_week} unit={c.unit} />
        <FlowRow label="本月" shipped={c.shipped_this_month} projected={c.projected_month} unit={c.unit} />
        <div className="flex items-baseline justify-between">
          <span className="text-muted">上月</span>
          <span className="text-ink">
            已出 <b>{c.shipped_last_month.toLocaleString()}</b> {c.unit}
          </span>
        </div>
      </div>
    </div>
  );
}

function FlowRow({
  label,
  shipped,
  projected,
  unit,
}: {
  label: string;
  shipped: number;
  projected: number;
  unit: string;
}) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-muted">{label}</span>
      <span className="text-ink">
        已出 <b>{shipped.toLocaleString()}</b> · 預計 <b className="text-primary">{projected.toLocaleString()}</b> {unit}
      </span>
    </div>
  );
}
